import { useEffect, useMemo, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { Search, X } from "lucide-react"
import { PageEmpty, PageHeader } from "@/components/page"
import { useCccdTeamGallery, type GalleryTeam } from "../../hooks/useCccdTeamGallery"
import { TeamCard } from "./TeamCard"
import { UNASSIGNED_CATEGORY, categoryLabel, normalize, searchHaystack } from "./teamUtils"

type SortKey = "name" | "category" | "city"

const SORTS: { value: SortKey; label: string }[] = [
  { value: "name", label: "A–Z" },
  { value: "category", label: "Categoría" },
  { value: "city", label: "Ciudad" },
]

const collator = new Intl.Collator("es-CO", { sensitivity: "base" })

const focusRing = "outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"

function compare(sort: SortKey, a: GalleryTeam, b: GalleryTeam): number {
  if (sort === "category") {
    const c = collator.compare(categoryLabel(a.categorySlug), categoryLabel(b.categorySlug))
    if (c !== 0) return c
  } else if (sort === "city") {
    // Teams without a city go last.
    if (!a.institutionCity !== !b.institutionCity) return a.institutionCity ? -1 : 1
    const c = collator.compare(a.institutionCity ?? "", b.institutionCity ?? "")
    if (c !== 0) return c
  }
  return collator.compare(a.name, b.name)
}

function SkeletonGrid() {
  return (
    <div role="status" aria-label="Cargando equipos" className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {Array.from({ length: 8 }, (_, i) => (
        <div key={i} className="motion-shimmer aspect-[4/3] rounded-xl border border-border bg-muted" />
      ))}
    </div>
  )
}

// Read-only competitive showcase — team patches/photos are managed by
// admins from gestion, not uploaded by captains from Academy (see
// openspec/changes/cccd-category-scoped-contest-info, cccd-team-showcase).
// Scoped to one specific CCCD event — see
// openspec/changes/academy-staff-unrestricted-access design.md Decision 3.
export function TeamGallery({ eventId, eventName }: { eventId: string; eventName?: string }) {
  const { teams, isLoading, error, refetch } = useCccdTeamGallery(eventId)
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null)
  const [query, setQuery] = useState("")
  const [sort, setSort] = useState<SortKey>("name")
  const searchRef = useRef<HTMLInputElement>(null)

  // Stagger entrance only on the first render that has cards.
  const entered = useRef(false)
  const animate = !entered.current
  useEffect(() => {
    if (teams.length > 0) entered.current = true
  }, [teams.length])

  const counts = useMemo(() => {
    const map = new Map<string, number>()
    for (const team of teams) {
      const key = team.categorySlug ?? UNASSIGNED_CATEGORY
      map.set(key, (map.get(key) ?? 0) + 1)
    }
    return map
  }, [teams])

  const categories = useMemo(
    () =>
      Array.from(counts.keys()).sort((a, b) =>
        collator.compare(
          categoryLabel(a === UNASSIGNED_CATEGORY ? null : a),
          categoryLabel(b === UNASSIGNED_CATEGORY ? null : b)
        )
      ),
    [counts]
  )

  const indexed = useMemo(() => teams.map((team) => ({ team, haystack: searchHaystack(team) })), [teams])

  const filteredTeams = useMemo(() => {
    const q = normalize(query.trim())
    const wantsUnassigned = categoryFilter === UNASSIGNED_CATEGORY
    return indexed
      .filter(({ team, haystack }) => {
        if (categoryFilter !== null && (wantsUnassigned ? team.categorySlug !== null : team.categorySlug !== categoryFilter))
          return false
        return q === "" || haystack.includes(q)
      })
      .map((x) => x.team)
      .sort((a, b) => compare(sort, a, b))
  }, [indexed, query, categoryFilter, sort])

  const hasFilters = query.trim() !== "" || categoryFilter !== null
  const clearFilters = () => {
    setQuery("")
    setCategoryFilter(null)
  }
  const ready = !isLoading && !error && teams.length > 0

  const chipClass = (active: boolean) =>
    `inline-flex min-h-9 items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors motion-reduce:transition-none ${focusRing} ${
      active ? "bg-foreground text-background" : "border border-border text-muted-foreground hover:text-foreground"
    }`

  return (
    <div className="space-y-6">
      <PageHeader
        title="Equipos"
        description={
          <>
            {eventName ? `${eventName}: ` : ""}conoce a los equipos rivales y a quienes los integran.
            {ready && (
              <>
                {" "}
                <span className="font-medium tabular-nums text-foreground">{teams.length} en total.</span>
              </>
            )}
          </>
        }
      />

      {isLoading ? (
        <SkeletonGrid />
      ) : error ? (
        <PageEmpty
          title="No pudimos cargar los equipos"
          description={error}
          variant="ruled"
          action={
            <button
              type="button"
              onClick={refetch}
              className={`motion-press inline-flex min-h-11 items-center rounded-lg bg-primary px-5 text-sm font-semibold text-primary-foreground ${focusRing}`}
            >
              Reintentar
            </button>
          }
        />
      ) : teams.length === 0 ? (
        <PageEmpty
          title="Todavía no hay equipos activos en este evento"
          description="Cuando los equipos se confirmen aparecerán aquí por categoría, con su foto e integrantes. Mientras tanto puedes revisar el reto de tu categoría."
          variant="ruled"
          action={
            <>
              <Link
                to={`/cccd/${eventId}`}
                className={`motion-press inline-flex min-h-11 items-center rounded-lg bg-primary px-5 text-sm font-semibold text-primary-foreground hover:brightness-110 ${focusRing}`}
              >
                Ver el reto de mi categoría
              </Link>
              <button
                type="button"
                onClick={refetch}
                className={`inline-flex min-h-11 items-center rounded-lg border border-border px-5 text-sm font-semibold text-foreground hover:bg-muted ${focusRing}`}
              >
                Actualizar
              </button>
            </>
          }
        />
      ) : (
        <>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div role="search" className="relative flex-1">
              <label htmlFor="team-search" className="sr-only">
                Buscar equipos por nombre, institución, ciudad, categoría o integrante
              </label>
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
              <input
                id="team-search"
                ref={searchRef}
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar equipo, institución, ciudad o integrante"
                autoComplete="off"
                className={`h-11 w-full rounded-lg border border-border bg-card pl-9 pr-11 text-sm text-foreground placeholder:text-muted-foreground [&::-webkit-search-cancel-button]:hidden ${focusRing}`}
              />
              {query !== "" && (
                <button
                  type="button"
                  aria-label="Borrar búsqueda"
                  onClick={() => {
                    setQuery("")
                    searchRef.current?.focus()
                  }}
                  className={`absolute right-1 top-1/2 flex size-9 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground hover:text-foreground ${focusRing}`}
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="team-sort" className="text-sm font-medium text-muted-foreground">
                Ordenar por
              </label>
              <select
                id="team-sort"
                value={sort}
                onChange={(e) => setSort(e.target.value as SortKey)}
                className={`h-11 rounded-lg border border-border bg-card px-3 text-sm text-foreground ${focusRing}`}
              >
                {SORTS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {categories.length > 1 && (
            <div role="group" aria-label="Filtrar por categoría" className="flex flex-wrap gap-2">
              <button type="button" aria-pressed={categoryFilter === null} onClick={() => setCategoryFilter(null)} className={chipClass(categoryFilter === null)}>
                Todas <span className="tabular-nums">({teams.length})</span>
              </button>
              {categories.map((key) => (
                <button
                  key={key}
                  type="button"
                  aria-pressed={categoryFilter === key}
                  onClick={() => setCategoryFilter(categoryFilter === key ? null : key)}
                  className={chipClass(categoryFilter === key)}
                >
                  {categoryLabel(key === UNASSIGNED_CATEGORY ? null : key)} <span className="tabular-nums">({counts.get(key)})</span>
                </button>
              ))}
            </div>
          )}

          <p aria-live="polite" className="text-sm text-muted-foreground">
            <span className="tabular-nums">
              {filteredTeams.length} de {teams.length}
            </span>{" "}
            equipos
          </p>

          {filteredTeams.length === 0 ? (
            <PageEmpty
              title={
                categoryFilter !== null
                  ? `Ningún equipo de ${categoryLabel(categoryFilter === UNASSIGNED_CATEGORY ? null : categoryFilter)} coincide con tu búsqueda`
                  : "Ningún equipo coincide con tu búsqueda"
              }
              description="Prueba con otro nombre, institución o integrante."
              variant="ruled"
              action={
                hasFilters ? (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className={`inline-flex min-h-11 items-center rounded-lg border border-border px-5 text-sm font-semibold text-foreground hover:bg-muted ${focusRing}`}
                  >
                    Limpiar filtros
                  </button>
                ) : undefined
              }
            />
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filteredTeams.map((team, i) => (
                <TeamCard key={team.id} team={team} eventId={eventId} index={i} animate={animate} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}
